import React, { useCallback, useEffect, useState } from "react"
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap"
import { toast } from "react-toastify"
import API from "../../common/API"
import { buildRecordPath } from "../../common/recordIdentifier"

interface ParteResumen {
  id: number
  nombre?: string | null
  rol?: string | null
  identificador?: string | null
  cedulaMatchStatus: number
}

interface SemiProcesadoItem {
  idAudiencia: number
  nuc?: string | null
  tribunal?: string | null
  sala?: string | null
  distritoJudicial?: string | null
  categoriaTribunal?: string | null
  materia?: string | null
  fechaDate?: string | null
  horaAudiencia?: string | null
  estadoRolAudiencia?: string | null
  urlAudiencia?: string | null
  categoriaSugerida: "PIP" | "PADEP"
  estado: "Pendiente" | "Aprobado" | "Descartado"
  cantidadImputados: number
  cantidadVictimas: number
  totalPartes: number
  imputados: ParteResumen[]
  victimas: ParteResumen[]
  resumen: string
}

interface StatsData {
  pendientes: number
  pip: number
  padep: number
  aprobados: number
  descartados: number
}

interface AudienciaDetailData {
  idAudiencia: number
  nuc?: string | null
  tribunal?: string | null
  sala?: string | null
  distritoJudicial?: string | null
  categoriaTribunal?: string | null
  materia?: string | null
  fechaDate?: string | null
  horaAudiencia?: string | null
  modalidad?: string | null
  estadoRolAudiencia?: string | null
  tipoResultado?: number | null
  urlAudiencia?: string | null
  direccionSala?: string | null
  categoriaSugerida: "PIP" | "PADEP"
  motivoSugerencia: string
  imputados: Array<{
    id: number
    nombre?: string | null
    rol?: string | null
    identificador?: string | null
    cedulaMatchStatus: number
    cedulaMatchCandidates?: number | null
    cedulaMatchedAt?: string | null
  }>
  victimas: Array<{
    id: number
    nombre?: string | null
    rol?: string | null
    identificador?: string | null
    cedulaMatchStatus: number
    cedulaMatchCandidates?: number | null
    cedulaMatchedAt?: string | null
  }>
  otros: Array<{
    id: number
    nombre?: string | null
    rol?: string | null
    identificador?: string | null
    cedulaMatchStatus: number
  }>
}

const CATEGORIA_OPTIONS = [
  { value: "ALL", label: "Todas las categorías" },
  { value: "PADEP", label: "Sugeridos PADEP (Cómplices / Víctimas)" },
  { value: "PIP", label: "Sugeridos PIP (Individual)" },
]

const ESTADO_OPTIONS = [
  { value: "Pendiente", label: "Pendientes de aprobación" },
  { value: "Aprobado", label: "Aprobados" },
  { value: "Descartado", label: "Descartados" },
  { value: "ALL", label: "Todos los estados" },
]

const AdminSemiProcesadosComponent: React.FC = () => {
  const [categoria, setCategoria] = useState("ALL")
  const [estado, setEstado] = useState("Pendiente")
  const [search, setSearch] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [rows, setRows] = useState<SemiProcesadoItem[]>([])
  const [stats, setStats] = useState<StatsData>({
    pendientes: 0,
    pip: 0,
    padep: 0,
    aprobados: 0,
    descartados: 0,
  })
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [quantity, setQuantity] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)

  // Selección múltiple
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [processingBulk, setProcessingBulk] = useState(false)
  const [viewMode, setViewMode] = useState<"table" | "cards">("table")

  // Modal de Detalle
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailData, setDetailData] = useState<AudienciaDetailData | null>(null)
  const [detailCategory, setDetailCategory] = useState<"PIP" | "PADEP">("PADEP")
  const [approvingSingle, setApprovingSingle] = useState(false)

  const fetchData = useCallback(
    async (nextPage: number, nextCat: string, nextEst: string, nextSearch: string, nextQty: number = quantity) => {
      setLoading(true)
      try {
        const payload = {
          categoria: nextCat,
          estado: nextEst,
          search: nextSearch.trim() || null,
          page: nextPage,
          quantity: nextQty,
        }
        const res = await API.postAction("AudienciasSemiProcesados/List", payload)
        if (res.data.status === "ok") {
          setRows(res.data.response?.items || [])
          if (res.data.response?.stats) {
            setStats(res.data.response.stats)
          }
          setTotalPages(res.data.quantityOfPages || 1)
          setTotalRecords(res.data.quantityOfRecords || 0)
        } else {
          toast.error(res.data.response || "Error al cargar registros semi-procesados")
        }
      } catch {
        toast.error("No se pudo conectar con el servidor de audiencias semi-procesadas")
      } finally {
        setLoading(false)
      }
    },
    [quantity]
  )

  useEffect(() => {
    fetchData(0, categoria, estado, "", quantity)
  }, [quantity])

  const runSearch = async () => {
    setSearch(searchInput)
    setPage(0)
    setSelectedIds([])
    await fetchData(0, categoria, estado, searchInput)
  }

  const clearSearch = async () => {
    setSearch("")
    setSearchInput("")
    setPage(0)
    setSelectedIds([])
    await fetchData(0, categoria, estado, "")
  }

  const changeCategoria = async (next: string) => {
    setCategoria(next)
    setPage(0)
    setSelectedIds([])
    await fetchData(0, next, estado, search)
  }

  const changeEstado = async (next: string) => {
    setEstado(next)
    setPage(0)
    setSelectedIds([])
    await fetchData(0, categoria, next, search)
  }

  // Manejo de multi-select
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allCurrentIds = rows.map((r) => r.idAudiencia)
      setSelectedIds(Array.from(new Set([...selectedIds, ...allCurrentIds])))
    } else {
      const currentIdsSet = new Set(rows.map((r) => r.idAudiencia))
      setSelectedIds(selectedIds.filter((id) => !currentIdsSet.has(id)))
    }
  }

  const handleToggleRow = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((x) => x !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const isAllCurrentSelected =
    rows.length > 0 && rows.every((r) => selectedIds.includes(r.idAudiencia))

  // Aprobación Masiva
  const handleBulkApprove = async (categoriaOverride: "AUTO" | "PIP" | "PADEP") => {
    if (selectedIds.length === 0) return
    setProcessingBulk(true)
    try {
      const res = await API.postAction("AudienciasSemiProcesados/Aprobar", {
        idAudiencias: selectedIds,
        categoria: categoriaOverride,
      })
      if (res.data.status === "ok") {
        toast.success(res.data.response?.mensaje || "Audiencias aprobadas correctamente")
        setSelectedIds([])
        await fetchData(page, categoria, estado, search)
      } else {
        toast.error(res.data.response || "No se pudieron aprobar las audiencias seleccionadas")
      }
    } catch (ex: any) {
      toast.error(ex.message || "Error al procesar la aprobación")
    } finally {
      setProcessingBulk(false)
    }
  }

  // Descarte Masivo
  const handleBulkDiscard = async () => {
    if (selectedIds.length === 0) return
    if (!window.confirm(`¿Está seguro de descartar ${selectedIds.length} audiencia(s) seleccionada(s)?`)) {
      return
    }
    setProcessingBulk(true)
    try {
      const res = await API.postAction("AudienciasSemiProcesados/Descartar", {
        idAudiencias: selectedIds,
        motivo: "Descarte manual por operador",
      })
      if (res.data.status === "ok") {
        toast.success(res.data.response?.mensaje || "Audiencias descartadas")
        setSelectedIds([])
        await fetchData(page, categoria, estado, search)
      } else {
        toast.error(res.data.response || "No se pudieron descartar las audiencias")
      }
    } catch (ex: any) {
      toast.error(ex.message || "Error al descartar audiencias")
    } finally {
      setProcessingBulk(false)
    }
  }

  // Abrir Modal de Detalle
  const openDetail = async (item: SemiProcesadoItem) => {
    setShowDetailModal(true)
    setDetailLoading(true)
    setDetailData(null)
    setDetailCategory(item.categoriaSugerida)
    try {
      const res = await API.getAction(`AudienciasSemiProcesados/Detail?idAudiencia=${item.idAudiencia}`)
      if (res.data.status === "ok") {
        setDetailData(res.data.response)
        setDetailCategory(res.data.response?.categoriaSugerida || "PIP")
      } else {
        toast.error(res.data.response || "No se pudo cargar el detalle del expediente")
      }
    } catch {
      toast.error("Error al consultar el detalle del expediente")
    } finally {
      setDetailLoading(false)
    }
  }

  // Aprobación Individual desde Detalle
  const handleSingleApprove = async () => {
    if (!detailData) return
    setApprovingSingle(true)
    try {
      const res = await API.postAction("AudienciasSemiProcesados/Aprobar", {
        idAudiencias: [detailData.idAudiencia],
        categoria: detailCategory,
      })
      if (res.data.status === "ok") {
        toast.success(res.data.response?.mensaje || "Expediente aprobado correctamente")
        setShowDetailModal(false)
        await fetchData(page, categoria, estado, search)
      } else {
        toast.error(res.data.response || "No se pudo aprobar el expediente")
      }
    } catch (ex: any) {
      toast.error(ex.message || "Error al aprobar expediente")
    } finally {
      setApprovingSingle(false)
    }
  }

  const handleSingleDiscard = async () => {
    if (!detailData) return
    if (!window.confirm("¿Desea descartar este expediente?")) return
    setApprovingSingle(true)
    try {
      const res = await API.postAction("AudienciasSemiProcesados/Descartar", {
        idAudiencias: [detailData.idAudiencia],
        motivo: "Descarte individual por operador",
      })
      if (res.data.status === "ok") {
        toast.success("Expediente descartado")
        setShowDetailModal(false)
        await fetchData(page, categoria, estado, search)
      } else {
        toast.error(res.data.response || "No se pudo descartar")
      }
    } catch (ex: any) {
      toast.error(ex.message)
    } finally {
      setApprovingSingle(false)
    }
  }

  const formatDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString("es-DO") : "—")

  return (
    <div className="p-3">
      {/* Cabecera */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-1 fw-bold text-dark">
            <i className="fas fa-clipboard-check text-primary me-2" />
            Semi-procesados — Audiencias Judiciales (PIP / PADEP)
          </h5>
          <p className="text-muted small mb-0">
            Casos y audiencias con imputados y partes identificadas en el padrón. Revise el detalle y apruebe de forma individual o en lote.
          </p>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => fetchData(page, categoria, estado, search)}
          disabled={loading}
        >
          <i className="fas fa-sync-alt me-1" />
          Actualizar
        </Button>
      </div>

      {/* Tarjetas de Métricas Rápidas */}
      <Row className="g-3 mb-3">
        <Col xs={6} md={3}>
          <Card
            className="border-0 shadow-sm rounded-3 clickable"
            style={{
              borderLeft: "4px solid #f59e0b",
              backgroundColor: estado === "Pendiente" ? "#fffbeb" : "#ffffff",
            }}
            onClick={() => changeEstado("Pendiente")}
          >
            <Card.Body className="p-2 d-flex align-items-center justify-content-between">
              <div>
                <small className="text-muted fw-semibold d-block">Pendientes</small>
                <span className="fs-5 fw-bold text-dark">{stats.pendientes.toLocaleString("es-DO")}</span>
              </div>
              <div className="badge bg-warning text-dark p-2 rounded-circle">
                <i className="fas fa-clock" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card
            className="border-0 shadow-sm rounded-3 clickable"
            style={{
              borderLeft: "4px solid #ef4444",
              backgroundColor: categoria === "PADEP" ? "#fef2f2" : "#ffffff",
            }}
            onClick={() => changeCategoria("PADEP")}
          >
            <Card.Body className="p-2 d-flex align-items-center justify-content-between">
              <div>
                <small className="text-muted fw-semibold d-block">Sugeridos PADEP</small>
                <span className="fs-5 fw-bold text-danger">{stats.padep.toLocaleString("es-DO")}</span>
              </div>
              <div className="badge bg-danger text-white p-2 rounded-circle">
                <i className="fas fa-user-secret" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card
            className="border-0 shadow-sm rounded-3 clickable"
            style={{
              borderLeft: "4px solid #3b82f6",
              backgroundColor: categoria === "PIP" ? "#eff6ff" : "#ffffff",
            }}
            onClick={() => changeCategoria("PIP")}
          >
            <Card.Body className="p-2 d-flex align-items-center justify-content-between">
              <div>
                <small className="text-muted fw-semibold d-block">Sugeridos PIP</small>
                <span className="fs-5 fw-bold text-primary">{stats.pip.toLocaleString("es-DO")}</span>
              </div>
              <div className="badge bg-primary text-white p-2 rounded-circle">
                <i className="fas fa-user-tag" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card
            className="border-0 shadow-sm rounded-3 clickable"
            style={{
              borderLeft: "4px solid #10b981",
              backgroundColor: estado === "Aprobado" ? "#ecfdf5" : "#ffffff",
            }}
            onClick={() => changeEstado("Aprobado")}
          >
            <Card.Body className="p-2 d-flex align-items-center justify-content-between">
              <div>
                <small className="text-muted fw-semibold d-block">Aprobados</small>
                <span className="fs-5 fw-bold text-success">{stats.aprobados.toLocaleString("es-DO")}</span>
              </div>
              <div className="badge bg-success text-white p-2 rounded-circle">
                <i className="fas fa-check-circle" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Barra de Filtros y Búsqueda */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="p-3">
          <Row className="g-2 align-items-end">
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="small fw-semibold mb-1 text-muted">Estado de Aprobación</Form.Label>
              <Form.Select
                size="sm"
                value={estado}
                onChange={(e) => changeEstado(e.target.value)}
              >
                {ESTADO_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="small fw-semibold mb-1 text-muted">Categoría</Form.Label>
              <Form.Select
                size="sm"
                value={categoria}
                onChange={(e) => changeCategoria(e.target.value)}
              >
                {CATEGORIA_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="small fw-semibold mb-1 text-muted">Buscar (NUC, Cédula, Nombre, Tribunal)</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  size="sm"
                  placeholder="Ej. 2024-001234, Cédula o Nombre"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") runSearch()
                  }}
                />
                <Button variant="dark" size="sm" onClick={runSearch} disabled={loading || !searchInput.trim()}>
                  <i className="fas fa-search" />
                </Button>
                {search && (
                  <Button variant="outline-secondary" size="sm" onClick={clearSearch} disabled={loading}>
                    <i className="fas fa-times" />
                  </Button>
                )}
              </div>
            </Col>
            <Col xs={12} md={2} className="d-flex align-items-center justify-content-between justify-content-md-end gap-2 mt-2 mt-md-0">
              <span className="text-muted small fw-semibold">
                {totalRecords.toLocaleString("es-DO")} caso(s)
              </span>
              <div className="btn-group btn-group-sm" role="group">
                <Button
                  variant={viewMode === "table" ? "primary" : "outline-secondary"}
                  size="sm"
                  onClick={() => setViewMode("table")}
                  title="Vista Tabla Compacta"
                >
                  <i className="fas fa-table" />
                </Button>
                <Button
                  variant={viewMode === "cards" ? "primary" : "outline-secondary"}
                  size="sm"
                  onClick={() => setViewMode("cards")}
                  title="Vista Tarjetas"
                >
                  <i className="fas fa-th-large" />
                </Button>
              </div>
            </Col>
          </Row>

          {/* Barra de Acciones Multi-select */}
          {selectedIds.length > 0 && (
            <div className="mt-3 p-2 bg-slate-100 rounded-3 border border-primary/20 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-primary fs-6">{selectedIds.length}</span>
                <strong className="small text-dark">expediente(s) seleccionado(s)</strong>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleBulkApprove("AUTO")}
                  disabled={processingBulk}
                >
                  <i className="fas fa-magic me-1" />
                  Aprobar según sugerencia
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleBulkApprove("PADEP")}
                  disabled={processingBulk}
                >
                  <i className="fas fa-user-secret me-1" />
                  Aprobar como PADEP
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleBulkApprove("PIP")}
                  disabled={processingBulk}
                >
                  <i className="fas fa-user-tag me-1" />
                  Aprobar como PIP
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleBulkDiscard}
                  disabled={processingBulk}
                >
                  <i className="fas fa-trash-alt me-1" />
                  Descartar
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setSelectedIds([])}
                  disabled={processingBulk}
                >
                  Cancelar selección
                </Button>
              </div>
            </div>
          )}

          {/* Vista Tabla Compacta (Zero Scroll Horizontal) */}
          {viewMode === "table" && (
            <div className="table-responsive mt-3" style={{ overflowX: "auto" }}>
              <Table hover size="sm" className="align-middle mb-0 text-break">
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: 36 }} className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={isAllCurrentSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={loading || rows.length === 0}
                      />
                    </th>
                    <th style={{ width: "28%" }}>Caso & Tribunal</th>
                    <th style={{ width: "26%" }}>Victimarios (Imputados)</th>
                    <th style={{ width: "22%" }}>Víctimas (Agraviados)</th>
                    <th style={{ width: "12%" }} className="text-center">Clasificación</th>
                    <th style={{ width: "12%" }} className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        <Spinner size="sm" className="me-2 text-primary" />
                        Cargando registros semi-procesados...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No se encontraron audiencias semi-procesadas con los filtros seleccionados.
                      </td>
                    </tr>
                  ) : (
                    rows.map((item) => {
                      const isSelected = selectedIds.includes(item.idAudiencia)
                      const isPadep = item.categoriaSugerida === "PADEP"
                      const countImp = item.cantidadImputados || item.imputados.length
                      const countVic = item.cantidadVictimas || item.victimas.length

                      return (
                        <tr
                          key={item.idAudiencia}
                          className={isSelected ? "table-primary/20" : ""}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleRow(item.idAudiencia)}
                        >
                          <td
                            className="text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleRow(item.idAudiencia)}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1 flex-wrap mb-1">
                              <span className="fw-bold font-mono text-dark" style={{ fontSize: "0.85rem" }} title="Número de Expediente / NUC">
                                <i className="fas fa-folder text-primary me-1" style={{ fontSize: "0.8rem" }} />
                                {item.nuc || `AUD-${item.idAudiencia}`}
                              </span>
                              {item.materia && (
                                <Badge
                                  bg={
                                    /penal/i.test(item.materia)
                                      ? "danger"
                                      : /laboral/i.test(item.materia)
                                      ? "warning text-dark"
                                      : "secondary"
                                  }
                                  className="font-normal"
                                  style={{ fontSize: "0.68rem" }}
                                >
                                  {item.materia}
                                </Badge>
                              )}
                              <span className="text-muted" style={{ fontSize: "0.72rem" }}>
                                <i className="far fa-calendar-alt me-1" />
                                {formatDate(item.fechaDate)}
                                {item.horaAudiencia ? ` ${item.horaAudiencia}` : ""}
                              </span>
                            </div>
                            <div className="text-dark small fw-medium">
                              {item.tribunal || "Tribunal no especificado"}
                            </div>
                            {(item.distritoJudicial || item.sala) && (
                              <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                                {[item.distritoJudicial, item.sala].filter(Boolean).join(" • ")}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1">
                              <div className="d-flex align-items-center gap-1">
                                <span className="badge bg-danger px-1.5 py-0.5" style={{ fontSize: "0.68rem" }}>
                                  <i className="fas fa-user-shield me-1" />
                                  {countImp} {countImp === 1 ? "Imputado" : "Imputados"}
                                </span>
                              </div>
                              {item.imputados.map((imp) => (
                                <div key={imp.id} className="d-flex align-items-center gap-1 flex-wrap">
                                  <span className="small fw-semibold text-dark" title={imp.nombre || ""}>
                                    {imp.nombre}
                                  </span>
                                  {imp.identificador && (
                                    <a
                                      href={buildRecordPath(imp.identificador)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="badge bg-primary text-decoration-none font-mono"
                                      onClick={(e) => e.stopPropagation()}
                                      title="Ver antecedentes"
                                      style={{ fontSize: "0.68rem" }}
                                    >
                                      {imp.identificador}
                                      <i className="fa fa-arrow-up-right-from-square ms-1" style={{ fontSize: "0.58rem" }} />
                                    </a>
                                  )}
                                </div>
                              ))}
                              {item.imputados.length === 0 && <span className="text-muted small">—</span>}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1">
                              <div className="d-flex align-items-center gap-1">
                                <span className="badge bg-secondary px-1.5 py-0.5" style={{ fontSize: "0.68rem" }}>
                                  <i className="fas fa-user me-1" />
                                  {countVic} {countVic === 1 ? "Víctima" : "Víctimas"}
                                </span>
                              </div>
                              {item.victimas.map((vic) => (
                                <div key={vic.id} className="d-flex align-items-center gap-1 flex-wrap">
                                  <span className="small text-dark" title={vic.nombre || ""}>
                                    {vic.nombre}
                                  </span>
                                  {vic.identificador && (
                                    <a
                                      href={buildRecordPath(vic.identificador)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="badge bg-secondary text-decoration-none font-mono"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ fontSize: "0.68rem" }}
                                    >
                                      {vic.identificador}
                                    </a>
                                  )}
                                </div>
                              ))}
                              {item.victimas.length === 0 && <span className="text-muted small">—</span>}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="d-flex flex-column align-items-center gap-1">
                              {isPadep ? (
                                <span
                                  className="badge fw-bold shadow-sm"
                                  style={{
                                    backgroundColor: "#fef2f2",
                                    border: "1.5px solid #dc2626",
                                    color: "#991b1b",
                                    fontSize: "0.72rem",
                                  }}
                                  title="Asociación / Co-imputados / Delito Precedente"
                                >
                                  <i className="fas fa-user-secret me-1" />
                                  PADEP
                                </span>
                              ) : (
                                <span
                                  className="badge fw-bold shadow-sm"
                                  style={{
                                    backgroundColor: "#eff6ff",
                                    border: "1.5px solid #2563eb",
                                    color: "#1e40af",
                                    fontSize: "0.72rem",
                                  }}
                                  title="Persona Imputada Individual"
                                >
                                  <i className="fas fa-user-tag me-1" />
                                  PIP
                                </span>
                              )}
                              {item.estado === "Aprobado" ? (
                                <Badge bg="success" style={{ fontSize: "0.68rem" }}>Aprobado</Badge>
                              ) : item.estado === "Descartado" ? (
                                <Badge bg="secondary" style={{ fontSize: "0.68rem" }}>Descartado</Badge>
                              ) : (
                                <Badge bg="warning" text="dark" style={{ fontSize: "0.68rem" }}>
                                  Pendiente
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="text-end" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="primary"
                              size="sm"
                              className="px-2 py-1"
                              onClick={() => openDetail(item)}
                              title="Ver detalles completos del caso y aprobar"
                            >
                              <i className="fas fa-search me-1" />
                              Revisar
                            </Button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}

          {/* Vista Tarjetas (Responsiva / Mobile & Tablet Friendly) */}
          {viewMode === "cards" && (
            <div className="mt-3">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner size="sm" className="me-2 text-primary" />
                  Cargando registros semi-procesados...
                </div>
              ) : rows.length === 0 ? (
                <div className="text-center text-muted py-4">
                  No se encontraron audiencias semi-procesadas con los filtros seleccionados.
                </div>
              ) : (
                <Row className="g-3">
                  {rows.map((item) => {
                    const isSelected = selectedIds.includes(item.idAudiencia)
                    const isPadep = item.categoriaSugerida === "PADEP"
                    const countImp = item.cantidadImputados || item.imputados.length
                    const countVic = item.cantidadVictimas || item.victimas.length

                    return (
                      <Col xs={12} lg={6} key={item.idAudiencia}>
                        <Card
                          className={`h-100 border shadow-sm rounded-3 transition-all ${
                            isSelected ? "border-primary bg-blue-50/20" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleRow(item.idAudiencia)}
                        >
                          <Card.Header className="bg-light py-2 px-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <Form.Check
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleRow(item.idAudiencia)}
                              />
                              <span className="fw-bold font-mono text-dark" title="Número de Expediente / NUC">
                                <i className="fas fa-folder text-primary me-1" style={{ fontSize: "0.8rem" }} />
                                {item.nuc || `AUD-${item.idAudiencia}`}
                              </span>
                              {item.materia && (
                                <Badge
                                  bg={
                                    /penal/i.test(item.materia)
                                      ? "danger"
                                      : /laboral/i.test(item.materia)
                                      ? "warning text-dark"
                                      : "secondary"
                                  }
                                  className="font-normal"
                                  style={{ fontSize: "0.68rem" }}
                                >
                                  {item.materia}
                                </Badge>
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              {isPadep ? (
                                <span className="badge bg-danger" style={{ fontSize: "0.68rem" }}>
                                  <i className="fas fa-user-secret me-1" />
                                  PADEP
                                </span>
                              ) : (
                                <span className="badge bg-primary" style={{ fontSize: "0.68rem" }}>
                                  <i className="fas fa-user-tag me-1" />
                                  PIP
                                </span>
                              )}
                              <Badge
                                bg={item.estado === "Aprobado" ? "success" : item.estado === "Descartado" ? "secondary" : "warning"}
                                text={item.estado === "Pendiente" ? "dark" : undefined}
                                style={{ fontSize: "0.68rem" }}
                              >
                                {item.estado}
                              </Badge>
                            </div>
                          </Card.Header>
                          <Card.Body className="p-3">
                            <div className="mb-2">
                              <div className="small fw-semibold text-dark">{item.tribunal || "Tribunal no especificado"}</div>
                              {(item.distritoJudicial || item.sala || item.fechaDate) && (
                                <div className="text-muted small">
                                  {[
                                    item.distritoJudicial,
                                    item.sala,
                                    item.fechaDate ? `${formatDate(item.fechaDate)} ${item.horaAudiencia || ""}` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </div>
                              )}
                            </div>

                            <Row className="g-2 pt-2 border-top">
                              <Col xs={12} sm={6}>
                                <small className="text-muted fw-bold d-block mb-1">
                                  <i className="fas fa-user-shield text-danger me-1" />
                                  Imputados ({countImp})
                                </small>
                                <div className="d-flex flex-column gap-1">
                                  {item.imputados.map((imp) => (
                                    <div key={imp.id} className="d-flex align-items-center gap-1 flex-wrap">
                                      <span className="small fw-semibold text-dark">{imp.nombre}</span>
                                      {imp.identificador && (
                                        <a
                                          href={buildRecordPath(imp.identificador)}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="badge bg-primary text-decoration-none font-mono"
                                          onClick={(e) => e.stopPropagation()}
                                          style={{ fontSize: "0.65rem" }}
                                        >
                                          {imp.identificador}
                                          <i className="fa fa-arrow-up-right-from-square ms-1" style={{ fontSize: "0.55rem" }} />
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                  {item.imputados.length === 0 && <span className="text-muted small">—</span>}
                                </div>
                              </Col>

                              <Col xs={12} sm={6}>
                                <small className="text-muted fw-bold d-block mb-1">
                                  <i className="fas fa-user text-secondary me-1" />
                                  Víctimas ({countVic})
                                </small>
                                <div className="d-flex flex-column gap-1">
                                  {item.victimas.map((vic) => (
                                    <div key={vic.id} className="d-flex align-items-center gap-1 flex-wrap">
                                      <span className="small text-dark">{vic.nombre}</span>
                                      {vic.identificador && (
                                        <a
                                          href={buildRecordPath(vic.identificador)}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="badge bg-secondary text-decoration-none font-mono"
                                          onClick={(e) => e.stopPropagation()}
                                          style={{ fontSize: "0.65rem" }}
                                        >
                                          {vic.identificador}
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                  {item.victimas.length === 0 && <span className="text-muted small">—</span>}
                                </div>
                              </Col>
                            </Row>
                          </Card.Body>
                          <Card.Footer className="bg-white py-2 px-3 d-flex align-items-center justify-content-end" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => openDetail(item)}
                              title="Ver detalles completos del caso y aprobar"
                            >
                              <i className="fas fa-search me-1" />
                              Revisar Caso
                            </Button>
                          </Card.Footer>
                        </Card>
                      </Col>
                    )
                  })}
                </Row>
              )}
            </div>
          )}

          {/* Paginación */}
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2 pt-2 border-top">
            <div className="d-flex align-items-center gap-2">
              <span className="small text-muted">Mostrar:</span>
              <Form.Select
                size="sm"
                style={{ width: "80px" }}
                value={quantity}
                onChange={async (e) => {
                  const newQty = parseInt(e.target.value, 10) || 20
                  setQuantity(newQty)
                  setPage(0)
                  await fetchData(0, categoria, estado, search, newQty)
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
              <small className="text-muted">por página</small>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page === 0 || loading}
                onClick={async () => {
                  const n = Math.max(0, page - 1)
                  setPage(n)
                  await fetchData(n, categoria, estado, search, quantity)
                }}
              >
                ‹ Anterior
              </Button>
              <small className="text-muted fw-semibold px-1">
                Página {totalPages === 0 ? 0 : page + 1} de {totalPages} ({totalRecords.toLocaleString("es-DO")} registros)
              </small>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={loading || page >= totalPages - 1}
                onClick={async () => {
                  const n = page + 1
                  setPage(n)
                  await fetchData(n, categoria, estado, search, quantity)
                }}
              >
                Siguiente ›
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Modal de Detalle Exhaustivo del Expediente */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
        className="bootstrap-scope"
      >
        <Modal.Header closeButton className="bg-light py-2">
          <Modal.Title className="h6 fw-bold mb-0">
            <i className="fas fa-gavel text-primary me-2" />
            Detalle del Expediente Judicial:{" "}
            <span className="text-dark font-mono">{detailData?.nuc || `ID ${detailData?.idAudiencia}`}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3">
          {detailLoading ? (
            <div className="text-center py-4">
              <Spinner size="sm" className="me-2 text-primary" />
              Cargando detalle del caso...
            </div>
          ) : !detailData ? (
            <div className="alert alert-warning mb-0">No se pudo obtener información del caso.</div>
          ) : (
            <div>
              {/* Información Técnica del Caso */}
              <Card className="border shadow-none mb-3 bg-slate-50">
                <Card.Body className="p-3">
                  <Row className="g-2 small">
                    <Col md={6}>
                      <strong className="text-muted d-block">Tribunal / Sala:</strong>
                      <span className="fw-semibold text-dark">
                        {detailData.tribunal || "—"} {detailData.sala ? `(${detailData.sala})` : ""}
                      </span>
                    </Col>
                    <Col md={6}>
                      <strong className="text-muted d-block">Distrito Judicial:</strong>
                      <span className="fw-semibold text-dark">{detailData.distritoJudicial || "—"}</span>
                    </Col>
                    <Col md={4}>
                      <strong className="text-muted d-block">Materia:</strong>
                      <span className="badge bg-primary">{detailData.materia || "OFICIAL"}</span>
                    </Col>
                    <Col md={4}>
                      <strong className="text-muted d-block">Fecha Audiencia:</strong>
                      <span className="fw-semibold text-dark">
                        {formatDate(detailData.fechaDate)} {detailData.horaAudiencia || ""}
                      </span>
                    </Col>
                    <Col md={4}>
                      <strong className="text-muted d-block">Estado Rol:</strong>
                      <span className="badge bg-dark">{detailData.estadoRolAudiencia || "Conocida"}</span>
                    </Col>
                    {detailData.urlAudiencia && (
                      <Col xs={12} className="mt-1">
                        <strong className="text-muted d-block">Enlace oficial:</strong>
                        <a
                          href={detailData.urlAudiencia}
                          target="_blank"
                          rel="noreferrer"
                          className="text-truncate d-block small text-decoration-none"
                        >
                          <i className="fa fa-link me-1" />
                          {detailData.urlAudiencia}
                        </a>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>

              {/* Sugerencia Heurística */}
              <div
                className={`alert ${
                  detailData.categoriaSugerida === "PADEP" ? "alert-danger" : "alert-primary"
                } py-2 mb-3 small d-flex align-items-center`}
              >
                <i
                  className={`fas ${
                    detailData.categoriaSugerida === "PADEP" ? "fa-user-secret" : "fa-user-tag"
                  } fa-lg me-2`}
                />
                <div>
                  <strong>Sugerencia de Clasificación: {detailData.categoriaSugerida}</strong>
                  <div>{detailData.motivoSugerencia}</div>
                </div>
              </div>

              {/* Desglose de Imputados */}
              <h6 className="fw-bold mb-2">
                <i className="fas fa-user-ninja text-danger me-2" />
                Imputados / Investigados ({detailData.imputados.length})
              </h6>
              <Table size="sm" bordered hover className="align-middle mb-3 small">
                <thead className="table-light">
                  <tr>
                    <th>Nombre</th>
                    <th>Rol</th>
                    <th>Identificador / Cédula</th>
                    <th>Estado Padrón</th>
                  </tr>
                </thead>
                <tbody>
                  {detailData.imputados.map((p) => (
                    <tr key={p.id}>
                      <td className="fw-bold text-dark">{p.nombre}</td>
                      <td>
                        <span className="badge bg-danger text-white">{p.rol || "Imputado"}</span>
                      </td>
                      <td>
                        {p.identificador ? (
                          <a
                            href={buildRecordPath(p.identificador)}
                            target="_blank"
                            rel="noreferrer"
                            className="badge bg-dark text-white text-decoration-none font-mono"
                          >
                            <i className="fa fa-id-card me-1 text-warning" />
                            {p.identificador}
                          </a>
                        ) : (
                          <span className="text-muted">Sin identificar</span>
                        )}
                      </td>
                      <td>
                        {p.identificador ? (
                          <span className="text-success fw-bold">
                            <i className="fa fa-check-circle me-1" />
                            Vinculado
                          </span>
                        ) : (
                          <span className="text-warning">
                            <i className="fa fa-clock me-1" />
                            Pendiente
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {detailData.imputados.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center text-muted">
                        No hay imputados registrados en esta audiencia.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Desglose de Víctimas */}
              <h6 className="fw-bold mb-2">
                <i className="fas fa-user-shield text-dark me-2" />
                Víctimas / Agraviados / Querellantes ({detailData.victimas.length})
              </h6>
              <Table size="sm" bordered hover className="align-middle mb-3 small">
                <thead className="table-light">
                  <tr>
                    <th>Nombre</th>
                    <th>Rol</th>
                    <th>Identificador</th>
                  </tr>
                </thead>
                <tbody>
                  {detailData.victimas.map((p) => (
                    <tr key={p.id}>
                      <td className="fw-semibold text-dark">{p.nombre}</td>
                      <td>
                        <span className="badge bg-dark text-white">{p.rol || "Víctima"}</span>
                      </td>
                      <td>
                        {p.identificador ? (
                          <a
                            href={buildRecordPath(p.identificador)}
                            target="_blank"
                            rel="noreferrer"
                            className="badge bg-secondary text-white text-decoration-none font-mono"
                          >
                            {p.identificador}
                          </a>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {detailData.victimas.length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center text-muted">
                        No hay víctimas o querellantes registrados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Selección de Categoría para Aprobación */}
              <Card className="border bg-light p-3">
                <Form.Label className="fw-bold small mb-2">
                  <i className="fas fa-tag me-1 text-primary" />
                  Seleccionar Categoría de Aprobación para este Caso:
                </Form.Label>
                <div className="d-flex gap-4">
                  <Form.Check
                    type="radio"
                    id="cat-padep"
                    name="modalCategory"
                    label={
                      <span>
                        <strong className="text-danger">PADEP</strong> (Asociación / Co-imputados / Delito Precedente)
                      </span>
                    }
                    checked={detailCategory === "PADEP"}
                    onChange={() => setDetailCategory("PADEP")}
                  />
                  <Form.Check
                    type="radio"
                    id="cat-pip"
                    name="modalCategory"
                    label={
                      <span>
                        <strong className="text-primary">PIP</strong> (Persona Imputada Individual)
                      </span>
                    }
                    checked={detailCategory === "PIP"}
                    onChange={() => setDetailCategory("PIP")}
                  />
                </div>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light py-2">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleSingleDiscard}
            disabled={approvingSingle || !detailData}
          >
            <i className="fas fa-trash-alt me-1" />
            Descartar Caso
          </Button>
          <div className="ms-auto d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowDetailModal(false)}
              disabled={approvingSingle}
            >
              Cerrar
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={handleSingleApprove}
              disabled={approvingSingle || !detailData}
            >
              {approvingSingle ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Aprobando...
                </>
              ) : (
                <>
                  <i className="fas fa-check-circle me-1" />
                  Aprobar como {detailCategory}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default AdminSemiProcesadosComponent
